import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { resolveDefaultModelId } from "@/lib/defaultModel";
import { modelService, teamService } from "@/lib/services";
import type { AIModel, ModelTierDefault, Team, TeamMembership } from "@/types";
import { useAuth } from "./AuthContext";

interface AppContextType {
  // Teams
  teams: Team[];
  teamMemberships: TeamMembership[];
  refreshTeams: () => Promise<void>;

  // Models
  models: AIModel[];
  enabledModels: AIModel[];
  defaultModelId: string | null;
  refreshModels: () => Promise<void>;

  // Tier defaults (user-facing only)
  tierDefaults: ModelTierDefault[];

  // Loading states
  isLoadingTeams: boolean;
  isLoadingModels: boolean;
}

const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { isAuthenticated, profile } = useAuth();

  // Teams state
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);

  // Models state
  const [models, setModels] = useState<AIModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(true);

  // Tier defaults state
  const [tierDefaults, setTierDefaults] = useState<ModelTierDefault[]>([]);

  // Derived state
  const teamMemberships = profile?.teams || [];
  const enabledModels = models.filter((m) => (m.isEnabled || m.is_enabled) && m.modelType === "chat");
  const preferredModelId = profile?.preferences?.defaultModelId;
  const defaultModelId = resolveDefaultModelId({ preferredModelId, tierDefaults, enabledModels });

  const refreshTeams = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoadingTeams(true);
      const response = await teamService.list();
      setTeams(response.data);
    } catch (err) {
      console.error("Failed to load teams:", err);
    } finally {
      setIsLoadingTeams(false);
    }
  }, [isAuthenticated]);

  const refreshModels = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoadingModels(true);
      const [modelsRes, tierRes] = await Promise.all([
        modelService.list(),
        modelService.listUserFacingTierDefaults().catch(() => null),
      ]);
      setModels(modelsRes.data);
      if (tierRes?.data?.defaults) {
        setTierDefaults(tierRes.data.defaults);
      }
    } catch (err) {
      console.error("Failed to load models:", err);
    } finally {
      setIsLoadingModels(false);
    }
  }, [isAuthenticated]);

  // Load teams and models when authenticated.
  // NOTE: Do NOT set isLoadingTeams/isLoadingModels to false in the else
  // branch.  During MSAL initialisation isAuthenticated starts as false and
  // flips to true once the token is resolved.  Setting loading=false while
  // teams=[] causes a flash where the Dashboard thinks teams have loaded
  // (empty) and hides the "Your Teams" section for one render frame.
  useEffect(() => {
    if (isAuthenticated) {
      refreshTeams();
      refreshModels();
    }
  }, [isAuthenticated, refreshTeams, refreshModels]);

  const value = useMemo<AppContextType>(
    () => ({
      teams,
      teamMemberships,
      refreshTeams,
      models,
      enabledModels,
      defaultModelId,
      refreshModels,
      tierDefaults,
      isLoadingTeams,
      isLoadingModels,
    }),
    [
      teams,
      teamMemberships,
      refreshTeams,
      models,
      enabledModels,
      defaultModelId,
      refreshModels,
      tierDefaults,
      isLoadingTeams,
      isLoadingModels,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextType {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}
